import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { CommissionSellerRow } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import {
  Button,
  CheckboxField,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Section,
  Select,
} from '@aeris/ui'

import {
  closeCommissionPeriod,
  periodLabel,
  shiftPeriod,
} from '../lib/api/commissions.ts'
import { fetchPaymentMethods } from '../lib/api/payments.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

import { CommissionPayeePicker } from './commission-payee-picker.tsx'

/**
 * Fechar o mês da comissão (F137).
 *
 * Mostra antes o que vai acontecer — quem entra, com quanto, e quem ficou de
 * fora por quê — e só então lança: um extrato congelado e um título a pagar
 * por vendedor, na filial em que a pessoa está, com o vencimento escolhido.
 *
 * A trava de "mês em andamento" está escrita aqui E no servidor: aqui para
 * explicar, lá para valer.
 *
 * Quem recebe se resolve aqui mesmo (F235): o gerente da Elite clicava em
 * fechar, o botão não andava, e o lugar de dizer quem recebe era Equipe → ⋯ →
 * Comissão, que ele não achou.
 *
 * E "já paguei" (F235): ele tinha pago as vendedoras antes de conseguir
 * fechar. Marcado, o título nasce pago no dia informado e não mexe no caixa.
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

const positive = (wire: string): boolean =>
  !wire.startsWith('-') && /[1-9]/.test(wire)

/** Como se paga comissão por fora do caixa — o servidor recusa as outras. */
const PAID_KINDS: readonly string[] = ['cash', 'pix', 'bank_transfer']

/** Hoje no relógio de quem olha: `2026-10-06`. */
const today = (): string => {
  const now = new Date()
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export const CommissionCloseDialog = ({
  period,
  periodOver,
  sellers,
  onClose,
}: {
  period: string
  periodOver: boolean
  sellers: readonly CommissionSellerRow[]
  onClose: () => void
}): ReactNode => {
  const { session, allows } = useAuth()
  const queryClient = useQueryClient()
  const branchId = session?.context?.branchId ?? null
  const branchName = session?.context?.branchName ?? null

  const [dueOn, setDueOn] = useState(`${shiftPeriod(period, 1)}-05`)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const canSettle = allows('finance.settle')
  const [alreadyPaid, setAlreadyPaid] = useState(false)
  const [paidOn, setPaidOn] = useState(today)
  const [methodId, setMethodId] = useState('')
  // Quem NÃO foi pago: o resto da lista entra marcado como pago.
  const [unpaid, setUnpaid] = useState<ReadonlySet<string>>(new Set())

  const methods = useQuery({
    queryKey: ['payment-methods', { status: 'active' }],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: alreadyPaid,
  })
  const paidMethods = (methods.data?.methods ?? []).filter((method) =>
    PAID_KINDS.includes(method.kind)
  )
  const chosenMethod =
    paidMethods.find((method) => method.id === methodId) ?? paidMethods[0]

  const candidates = sellers.filter(
    (row) => row.statement === null && positive(row.pending.commissionAmount)
  )
  const alreadyClosed = sellers.filter((row) => row.statement !== null)
  const withoutPayee = candidates.filter((row) => row.payee === null)
  const paidIds = alreadyPaid
    ? candidates
        .filter((row) => !unpaid.has(row.userId))
        .map((row) => row.userId)
    : []

  const blocker = !periodOver
    ? 'O mês ainda não acabou.'
    : candidates.length === 0
      ? 'Não há comissão a fechar neste mês.'
      : withoutPayee.length > 0
        ? 'Falta dizer quem recebe a comissão de alguém.'
        : branchId === null
          ? 'Escolha uma filial no topo da tela.'
          : dueOn === ''
            ? 'Escolha o vencimento.'
            : paidIds.length > 0 && paidOn === ''
              ? 'Escolha o dia em que pagou.'
              : paidIds.length > 0 && chosenMethod === undefined
                ? 'Escolha como pagou.'
                : null

  const close = useMutation({
    mutationFn: async () =>
      await closeCommissionPeriod({
        period,
        branchId: branchId ?? '',
        dueOn,
        paid:
          paidIds.length === 0 || chosenMethod === undefined
            ? null
            : {
                paidOn,
                paymentMethodId: chosenMethod.id,
                userIds: paidIds,
              },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['commissions'] })
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível fechar o mês.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Fechar a comissão de ${periodLabel(period)}`}
        description="Cada vendedor ganha um extrato congelado e um título a pagar no Financeiro."
        className="max-w-xl"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (blocker !== null) return
            setError(null)
            close.mutate()
          }}
        >
          {periodOver ? null : (
            <InlineAlert tone="warning" title="O mês ainda não acabou">
              A comissão fecha depois do último dia, para não deixar de fora a
              venda da última noite. Até lá, os números são uma prévia.
            </InlineAlert>
          )}

          {candidates.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum vendedor com comissão a fechar neste mês.
            </p>
          ) : (
            <ul
              aria-label="Quem entra no fechamento"
              className="flex flex-col divide-y divide-(--border-subtle) rounded-(--radius-md) border border-(--border-subtle) px-3"
            >
              {candidates.map((row) => (
                <li
                  key={row.userId}
                  className="flex flex-col gap-2 py-2 text-(length:--text-sm)"
                >
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex flex-col">
                      <span className="font-(--weight-medium)">{row.name}</span>
                      <span
                        className={
                          row.payee === null
                            ? 'text-(length:--text-xs) text-(--danger)'
                            : 'text-(length:--text-xs) text-(--text-muted)'
                        }
                      >
                        {row.payee === null
                          ? 'falta dizer quem recebe — escolha ou cadastre abaixo'
                          : `título para ${row.payee.name}`}
                      </span>
                    </span>
                    <span className="text-right font-(--font-mono) tabular-nums">
                      <strong>{money(row.pending.commissionAmount)}</strong>
                      <span className="block text-(length:--text-xs) text-(--text-muted)">
                        {`base ${money(row.pending.baseAmount)}`}
                      </span>
                    </span>
                  </span>
                  {alreadyPaid ? (
                    <CheckboxField
                      label="Já recebeu"
                      checked={!unpaid.has(row.userId)}
                      onCheckedChange={(checked) => {
                        setUnpaid((current) => {
                          const next = new Set(current)
                          if (checked === true) next.delete(row.userId)
                          else next.add(row.userId)
                          return next
                        })
                      }}
                    />
                  ) : null}
                  {row.payee === null ? (
                    <CommissionPayeePicker
                      userId={row.userId}
                      displayName={row.name}
                      payee={null}
                      label={`Quem recebe a comissão de ${row.name}`}
                      onSaved={(message) => {
                        setError(null)
                        setNotice(message)
                      }}
                      onError={(message) => {
                        setNotice(null)
                        setError(message)
                      }}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {alreadyClosed.length === 0 ? null : (
            <p className="text-(length:--text-xs) text-(--text-muted)">
              {`Já fechado: ${alreadyClosed.map((row) => row.name).join(', ')}.`}
            </p>
          )}

          {canSettle && candidates.length > 0 ? (
            // A caixa do "já paguei" é a Section do kit (F248), e não uma
            // moldura feita à mão — o teste de consistência pedia isto.
            <Section>
              <CheckboxField
                label="Já paguei a comissão"
                checked={alreadyPaid}
                onCheckedChange={(checked) => {
                  setAlreadyPaid(checked === true)
                }}
              />
              {alreadyPaid ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Pago em">
                      {(field) => (
                        <DateField
                          {...field}
                          value={paidOn}
                          onChange={(event) => {
                            setPaidOn(event.target.value)
                          }}
                        />
                      )}
                    </Field>
                    <Field label="Como pagou">
                      {(field) => (
                        <Select
                          {...field}
                          value={chosenMethod?.id ?? ''}
                          onChange={(event) => {
                            setMethodId(event.target.value)
                          }}
                        >
                          {paidMethods.length === 0 ? (
                            <option value="">Carregando…</option>
                          ) : null}
                          {paidMethods.map((method) => (
                            <option key={method.id} value={method.id}>
                              {method.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </div>
                  <p className="text-(length:--text-xs) text-(--text-muted)">
                    O título de quem já recebeu nasce pago nesse dia. Dinheiro
                    aqui não mexe no caixa: registra o que já saiu da gaveta no
                    dia do pagamento.
                  </p>
                </>
              ) : null}
            </Section>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Vencimento do título">
              {(field) => (
                <DateField
                  {...field}
                  value={dueOn}
                  onChange={(event) => {
                    setDueOn(event.target.value)
                  }}
                />
              )}
            </Field>
            <div className="flex flex-col gap-1 text-(length:--text-sm)">
              <span className="text-(--text-secondary)">
                Filial do lançamento
              </span>
              <span className="font-(--weight-medium)">
                {branchName ?? 'nenhuma escolhida'}
              </span>
            </div>
          </div>

          {notice === null ? null : (
            <InlineAlert tone="success">{notice}</InlineAlert>
          )}
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter className="items-center justify-between">
            <span className="text-(length:--text-xs) text-(--text-muted)">
              {blocker ?? ''}
            </span>
            <span className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={blocker !== null}
                loading={close.isPending}
              >
                {paidIds.length > 0
                  ? 'Fechar e registrar o pagamento'
                  : candidates.length <= 1
                    ? 'Fechar e lançar o título'
                    : `Fechar e lançar ${String(candidates.length)} títulos`}
              </Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
